// @ts-strict-ignore
import type {Location} from 'react-router';

import {QueryTrackingProps} from 'common/report/util/query-tracking/models/types';
import {FlowsResponse} from 'common/report/funnels/models/api-types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DateRangeType} from 'common/widgets/date-range-picker/models/types';
import {InsightsQueryResults, isInsightsBookmarkParams} from 'common/report/insights/util/types';
import {ChartType, InsightsBookmarkParams, InsightsResourceType} from 'common/types/reports/bookmark';
import {isAddiction} from 'common/report/retention/util';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {diffFromNow, getNow} from 'common/util/timing';
import {queryModule} from 'common/report/report-manager/query';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {Bookmark, Dashboard, DashboardCard, DashboardQueryOrigin, TimeFilter} from 'common/report/dashboards/types';
import {getBookmarkParamsWithOverrides} from 'common/report/dashboards/bookmark-overrides';
import {BaseQueryErrorType, BaseQueryResultsType} from 'common/report/types';
import {REPORT_TYPE_TO_REPORT_NAME} from 'apps/boards/src/models/constants';
import {Segmentation} from 'common/types/reports/query';
import {CurrentProject} from 'common/report/model-resources/projects';
import {getBehaviorShowClauses} from 'common/report/insights/models/utils';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {hasValidMetricBookmarkShowClauses} from 'common/report/insights/contexts/validation';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {getQueryLimit} from 'common/report/insights/query';
import {getLocation} from 'common/util/url';
import type {LocalizationStore} from 'common/i18n/store';
import {DashboardCardChartOptions} from 'common/report/dashboards/types';
import {FlowsBookmark} from 'common/report/flows/models/types';

import {BoardFilter} from '../dashboards-app/components/global-filter-bar/types';
import {getActiveBoardFilters} from '../dashboards-app/util';
import {checkBookmarkFilterOverridesSupported, getChartOptions, getDatasetName, isMultiMetricBookmark} from './index';
import * as InsightsReport from './insights';
import * as FlowsReport from './flows';

const setEndPoint = (bookmark: Bookmark<any>, queryMetadata: boolean, bookmarkType: BookmarkType) => {
  switch (bookmarkType) {
    case BookmarkType.Insights: {
      return queryMetadata ? `insights/query_metadata` : `insights`;
    }
    case BookmarkType.Flows:
    case BookmarkType.Funnels: {
      return queryMetadata ? `arb_funnels/query_metadata` : `arb_funnels`;
    }
    case BookmarkType.Retention: {
      if (isAddiction(bookmark.params)) {
        return queryMetadata ? `retention/frequency/query_metadata` : `retention/frequency`;
      }
      return queryMetadata ? `retention/query_metadata` : `retention`;
    }
    default:
      throw new Error(`unknown query-api endpoint`);
  }
};

// Query the card bookmark in the same(ish) way that the canonical report would query the bookmark.
// All card types are planned to move to "canonical" queries with minimal dashboard-specific logic.
export async function queryCanonicalBookmark({
  card,
  currentProject,
  dashboard,
  location,
  globalFilters,
  globalBreakdowns,
  timeFilterOverride,
  useQueryCache,
  setWorkspaceId = true,
  abortSignal,
  queryOverride,
  queryOrigin,
  queryMetadata = false,
  metricsResource,
  behaviorsResource,
  isBackgroundRepoll = false,
  overrideHeaders = {},
}: {
  card: DashboardCard;
  currentProject: CurrentProject;
  dashboard: Pick<Dashboard, `id`>;
  location: Location;
  globalFilters: BoardFilter[];
  globalBreakdowns?: Segmentation[];
  timeFilterOverride: TimeFilter | null;
  setWorkspaceId?: boolean;
  useQueryCache: boolean;
  abortSignal: null | AbortSignal;
  // TODO: stricter definition or better setup to allow public dashboards to use this function
  queryOverride?: any;
  queryOrigin: DashboardQueryOrigin;
  queryMetadata?: boolean;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  isBackgroundRepoll?: boolean;
  overrideHeaders?: Record<string, string>;
}): Promise<BaseQueryResultsType> {
  const datasetName = getDatasetName(card.bookmark);
  const chartOptions = getChartOptions({
    bookmark: card.bookmark,
    chartOptionsOverride: card.chart_options,
    metricsResource,
    behaviorsResource,
    currentProject,
  });
  const isMMBookmark = (card.bookmark.params?.sections?.show || [{}]).some(
    (item) => `behavior` in item || `measurement` in item,
  );
  const bookmarkType = isMMBookmark ? BookmarkType.Insights : card.bookmark.type;

  const bookmarkParams = getBookmarkParamsWithOverrides({
    bookmarkParams: card.bookmark.params,
    bookmarkType,
    chartTypeOverride: chartOptions.type,
    timeFilterOverride,
    additionalFilters: getActiveBoardFilters(globalFilters),
    additionalBreakdowns: globalBreakdowns,
  });

  const endpoint = setEndPoint(card.bookmark, queryMetadata, bookmarkType);
  const workspaceId = setWorkspaceId ? card.bookmark.workspace_id : null;

  const queryParams = {
    ...(workspaceId ? {workspace_id: workspaceId.toString()} : {}),
    project_id: card.bookmark.project_id.toString(),
    query_origin: queryOrigin,
  };

  const extraPostParams = (() => {
    switch (card.bookmark.type) {
      // TODO: consolidate the way query-api takes limits for different reports
      // instead of applying to bookmark at runtime for funnels/retention
      case BookmarkType.Insights:
      case BookmarkType.Funnels:
      case BookmarkType.Retention:
      case BookmarkType.RetentionFrequency:
        if (
          getBehaviorShowClauses(card.bookmark.params.sections.show).some(
            (clause) => clause.behavior.resourceType === InsightsResourceType.Cohorts,
          )
        ) {
          return {};
        }
        return {
          queryLimits: {
            limit: getQueryLimit(bookmarkParams),
          },
          report_query_origin: card.bookmark.type,
        };
      case BookmarkType.Flows:
        return {query_type: chartOptions.type === ChartType.FlowsTopPaths ? `flows_top_paths` : `flows_sankey`};
      // Added for linting rule
      default:
        return {};
    }
  })();

  const trackingProps: QueryTrackingProps = {
    bookmark_id: card.bookmark.id,
    dashboard_card_id: String(card.id),
    dashboard_id: dashboard.id,
    dashboard_query_origin: queryOrigin,
    is_main_query_for_report: true,
    queried_from_dashboards: true,
    is_background_repoll: isBackgroundRepoll,
    report_name: REPORT_TYPE_TO_REPORT_NAME[card.bookmark.type],
    request_url: getLocation().href,
  };

  const postPayload = {
    bookmark: bookmarkParams,
    ...(datasetName ? {dataset_name: datasetName} : {}),
    use_query_cache: useQueryCache,
    ...extraPostParams,
    tracking_props: trackingProps,
    dashboard_id: dashboard.id,
  };

  // Public Dashboards uses queryOverride to fetch card query data
  if (queryOverride) {
    return queryOverride(endpoint, queryParams, postPayload);
  } else {
    const response = await queryModule.executeReportQuery({
      endpoint,
      queryParams,
      overrideHeaders,
      body: postPayload,
      location,
      abortSignal,
    });
    return response.json();
  }
}

// result of the query from the dashboard card's bookmark
export interface DashboardCardResult {
  bookmarkId?: number;
  cardEventsInfo?: BaseQueryResultsType;
  chartError?: BaseQueryErrorType;
  chartLoaded: boolean;
  chartParams: any;
  fromDate?: string;
  dataGroupNotSupported: boolean;
  dataGroupNotSupportedReason?: string;
  invalidBookmark: boolean;
  notSupported: boolean;
  placeholder?: string;
  queryLastUpdated?: string; // should be in ISO date format
  queryResponseTimeMs: number;
  timeFilter?: TimeFilter;
  timeoutError?: boolean;
  toDate?: string;
}

export function getCardResultIfInvalid(card: DashboardCard, globalFilters: Segmentation[]): DashboardCardResult | null {
  const defaultResult: DashboardCardResult = {
    invalidBookmark: false,
    dataGroupNotSupported: false,
    notSupported: false,
    queryLastUpdated: ``,
    queryResponseTimeMs: 0,

    chartLoaded: false,
    chartParams: null,
  };

  const isInvalidMutliMetricBookmark =
    card.bookmark.params &&
    isMultiMetricBookmark(card.bookmark.params) &&
    !hasValidMetricBookmarkShowClauses(card.bookmark.params.sections.show);
  if (card.bookmark.error || card.bookmark.params?.error || isInvalidMutliMetricBookmark) {
    return {...defaultResult, invalidBookmark: true};
  }

  const {dataGroupNotSupported, dataGroupNotSupportedReason} = checkBookmarkFilterOverridesSupported(
    card.bookmark,
    globalFilters,
  );
  if (dataGroupNotSupported) {
    return {...defaultResult, dataGroupNotSupported, dataGroupNotSupportedReason};
  }

  return null;
}

function getAdjustedTimeFilter(timeFilterOverride: TimeFilter): TimeFilter {
  // GOOT: the "between $start_of_current_day -> $end_of_current_day" date range creates query-api bugs for compare-to-past reports
  // and has already been substituted in other reports due to the potential UX confusion that occurs when the incomplete today is compared to a past complete day.
  // For now as a quick fix, detect and replace this problematic date range when loading reports.
  // TODO: make this all consistent when unifying charts across dashboards and reports
  let adjustedTimeFilterOverride: TimeFilter = timeFilterOverride;
  if (
    adjustedTimeFilterOverride?.dateRange &&
    adjustedTimeFilterOverride.dateRange.type === DateRangeType.Between &&
    adjustedTimeFilterOverride.dateRange.from === `$start_of_current_day` &&
    adjustedTimeFilterOverride.dateRange.to === `$end_of_current_day`
  ) {
    const dateRange = new DateRange({
      type: DateRangeType.Since,
      from: `$start_of_current_day`,
    });
    adjustedTimeFilterOverride = {dateRange, displayText: dateRange.getDisplayText()};
  }

  return adjustedTimeFilterOverride;
}

export function getChartDescription({
  bookmark,
  dataGroups,
  timeFilter,
  results,
  chartOptions,
  i18n,
}: {
  bookmark: Bookmark<InsightsBookmarkParams | FlowsBookmark>;
  dataGroups: DataGroup[];
  timeFilter: TimeFilter;
  chartOptions: DashboardCardChartOptions;
  results: BaseQueryResultsType;
  behaviorsResource: BehaviorsResource;
  i18n: LocalizationStore;
}) {
  const type: BookmarkType = isInsightsBookmarkParams(bookmark.params) ? BookmarkType.Insights : bookmark.type;

  switch (type) {
    case BookmarkType.Insights: {
      return InsightsReport.getChartDescription({
        bookmark: {
          ...bookmark,
          params: bookmark.params as InsightsBookmarkParams,
        },
        results: results as InsightsQueryResults,
        chartOptions,
        i18n,
        timeFilter,
        dataGroups,
      });
    }
    case BookmarkType.Flows: {
      return FlowsReport.getChartDescription({
        bookmark: bookmark as Bookmark<FlowsBookmark>,
        timeFilter,
        i18n,
      });
    }
  }
  throw new Error(`chart description not implemented`);
}

function chartParamsFromResults(card: DashboardCard, results: BaseQueryResultsType): BaseQueryResultsType | null {
  let isEmpty = false;
  const bookmarkType = (card.bookmark?.params?.sections?.show || [{}]).some(
    (item) => `behavior` in item || `measurement` in item,
  )
    ? BookmarkType.Insights
    : card.bookmark.type;
  switch (bookmarkType) {
    case BookmarkType.Flows:
      isEmpty = FlowsReport.isEmptyFlowsResult(results as FlowsResponse);
      break;
    case BookmarkType.Insights:
      isEmpty = InsightsReport.isEmptyInsightsResult(results as InsightsQueryResults);
      break;
    default:
      throw new Error(`Unsupported report type`);
  }
  // return null chartParams so that dashboards handle empty state.
  // this is important to make sure dashboard digests can detect emptiness.
  return isEmpty ? null : results;
}

export async function queryCard({
  card,
  currentProject,
  dashboard,
  location,
  globalFilters,
  globalBreakdowns,
  timeFilterOverride,
  useQueryCache,
  abortSignal,
  queryOverride,
  queryOrigin,
  overrideHeaders = {},
  metricsResource,
  behaviorsResource,
}: {
  card: DashboardCard;
  currentProject: CurrentProject;
  dashboard: Dashboard;
  location: Location;
  globalFilters: BoardFilter[];
  globalBreakdowns?: Segmentation[];
  timeFilterOverride: TimeFilter | null;
  useQueryCache: boolean;
  abortSignal: null | AbortSignal;
  // TODO: stricter definition or better setup to allow public dashboards to use this function
  queryOverride?: any;
  queryOrigin: DashboardQueryOrigin;
  overrideHeaders?: Record<string, string>;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}): Promise<DashboardCardResult> {
  const invalidCardResult = getCardResultIfInvalid(card, globalFilters);
  if (invalidCardResult) {
    return invalidCardResult;
  }

  const adjustedTimeFilterOverride = getAdjustedTimeFilter(timeFilterOverride);

  const start = getNow();
  const results = await queryCanonicalBookmark({
    card,
    currentProject,
    dashboard,
    globalFilters,
    globalBreakdowns,
    location,
    timeFilterOverride: adjustedTimeFilterOverride,
    useQueryCache,
    abortSignal,
    queryOverride,
    queryOrigin,
    metricsResource,
    behaviorsResource,
    overrideHeaders,
  });
  const queryResponseTimeMs = Math.round(diffFromNow(start));

  const chartParams = chartParamsFromResults(card, results);

  return {
    bookmarkId: card.bookmark.id,
    chartParams,
    chartLoaded: true,
    queryLastUpdated: results.computed_at,
    queryResponseTimeMs,
    invalidBookmark: false,
    dataGroupNotSupported: false,
    notSupported: false,
    timeFilter: dashboard.time_filter,
  };
}

export function createCardResults({
  card,
  results,
  queryResponseTimeMs,
}: {
  card: DashboardCard;
  results: BaseQueryResultsType;
  queryResponseTimeMs: number;
}): DashboardCardResult {
  const chartParams = chartParamsFromResults(card, results);
  const fromDate = results?.date_range?.from_date;
  const toDate = results?.date_range?.to_date;

  return {
    bookmarkId: card.bookmark.id,
    chartLoaded: true,
    // return null chartParams so that dashboards handle empty state.
    // this is important to make sure dashboard digests can detect emptiness.
    chartParams,
    dataGroupNotSupported: false,
    fromDate,
    invalidBookmark: false,
    notSupported: false,
    queryLastUpdated: results.computed_at,
    queryResponseTimeMs,
    toDate,
  };
}
